import { supabase } from '@/lib/supabase';

export type CadenceOption = { days: number; label: string };

/**
 * The system-wide fallback cadence. A contact whose next_contact_interval_days is
 * NULL follows this value — the NULL is not backfilled, so changing this constant
 * moves every contact that never had an explicit override.
 */
export const DEFAULT_CADENCE_DAYS = 30;

/** Mirrors the fallback in useWorkspaceSettings when a workspace has no row yet. */
export const DEFAULT_CADENCE_OPTIONS: CadenceOption[] = [
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
  { days: 30, label: '1 month' },
  { days: 60, label: '2 months' },
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 360, label: '1 year' },
];

/** NULL/absent/nonsense -> the system default. Never throws, never yields an invalid date. */
export function resolveCadenceDays(interval: number | null | undefined): number {
  if (typeof interval !== 'number' || !Number.isFinite(interval) || interval <= 0) {
    return DEFAULT_CADENCE_DAYS;
  }
  return Math.round(interval);
}

/**
 * Calendar-day addition, not epoch arithmetic — setDate() keeps the wall-clock day
 * count correct across DST transitions, where adding 30*86400000ms would be off by an
 * hour and can land on the wrong calendar day.
 */
export function addDays(from: Date, days: number): Date {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

export function formatCadence(days: number): string {
  return days === 1 ? 'every day' : `every ${days} days`;
}

/**
 * Human label for a cadence, preferring the workspace option's own wording.
 *
 * The user picks a chip labelled "6 months"; showing them "every 180 days" is a mismatch
 * between what they chose and what they are told. Falls back to a day count only when the
 * value has no matching option (e.g. an admin removed it).
 */
export function formatCadenceLabel(days: number, options: CadenceOption[]): string {
  const match = options.find((o) => o.days === days);
  return match ? `every ${match.label.toLowerCase()}` : formatCadence(days);
}

/**
 * The visible chip set: the first `visible` workspace options, with the contact's current
 * cadence always pinned into view — including when the workspace admin has removed the
 * option the contact was set to. Everything else lives behind "More…".
 */
export function pickChipOptions(
  options: CadenceOption[],
  currentDays: number,
  visible = 4,
): CadenceOption[] {
  const head = options.slice(0, visible);
  if (head.some((o) => o.days === currentDays)) return head;

  const current = options.find((o) => o.days === currentDays) ?? {
    days: currentDays,
    label: `${currentDays} days`,
  };

  return [...head.slice(0, visible - 1), current].sort((a, b) => a.days - b.days);
}

/**
 * The one and only cadence write.
 *
 * Writes next_contact ONLY. Post-PR-0 the daily list gates the four identity tiers on
 * next_contact, which the update_contact_last_contacted trigger already maintains.
 * skip_until is not written by anything any more (issue #772 drops it) — do not
 * reintroduce it here.
 *
 * The write is unconditional and overrides the trigger's preserve-a-future-date branch:
 * you just spoke to this contact, so the clock restarts at their cadence even if they
 * were previously skipped far out.
 *
 * isExplicit: true only when the user actively picked an interval. Applying a cadence and
 * LEARNING a cadence are separate events; only the second needs an active choice. Logging
 * a call without touching the chips moves the date but leaves the override NULL, so the
 * contact keeps following the system default.
 */
export async function applyNextContact(
  contactId: string,
  days: number,
  opts: { isExplicit: boolean },
): Promise<Date> {
  const nextContact = addDays(new Date(), days);

  const updates: Record<string, string | number> = {
    next_contact: nextContact.toISOString(),
  };
  if (opts.isExplicit) {
    updates.next_contact_interval_days = days;
  }

  const { error } = await supabase.from('contacts').update(updates).eq('id', contactId);
  if (error) throw error;

  return nextContact;
}
